"use client"

import { useEffect, useState } from "react"
import { getStaff, updateStaff, softDeleteStaff } from "../../../services/AdminService"
import { FaEdit, FaTrash, FaSave, FaDownload, FaTimes, FaUsers, FaSearch } from "react-icons/fa"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

const sanitizeNameInput = (value) =>
  String(value || "")
    .replace(/[^a-zA-Z\s]/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^\s+/, "")

const AllStaff = () => {
  const [staffList, setStaffList] = useState([])
  const [filteredStaff, setFilteredStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState(null)
  const [editedStaff, setEditedStaff] = useState({})
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [validationErrors, setValidationErrors] = useState({})
  const [isUpdating, setIsUpdating] = useState(false)

  const clientId = sessionStorage.getItem("clientId")
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  useEffect(() => {
    if (clientId) {
      fetchStaff()
    }
  }, [clientId])

  useEffect(() => {
    filterStaff()
  }, [staffList, searchTerm, statusFilter])

  const fetchStaff = async () => {
    try {
      setLoading(true)
      const response = await getStaff()
      if (Array.isArray(response)) {
        setStaffList(response)
      } else if (response?.status === "success" && typeof response.data === "object") {
        const staffArray = Object.values(response.data)
        setStaffList(staffArray)
      } else {
        toast.error("Invalid response format.")
        setStaffList([])
      }
    } catch (error) {
      toast.error("Error fetching staff data. Please try again.")
      setStaffList([])
    } finally {
      setLoading(false)
    }
  }

  const filterStaff = () => {
    let filtered = staffList

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (staff) =>
          staff.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          staff.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          staff.mobile?.includes(searchTerm),
      )
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((staff) => staff.status === Number.parseInt(statusFilter))
    }

    setFilteredStaff(filtered)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const validateStaffData = (staff) => {
    const errors = {}

    // Name validation
    if (!staff.user_name?.trim()) {
      errors.user_name = "Name is required"
    } else if (staff.user_name.trim().length < 2) {
      errors.user_name = "Name must be at least 2 characters"
    } else if (staff.user_name.trim().length > 50) {
      errors.user_name = "Name must be less than 50 characters"
    } else if (!/^[a-zA-Z\s]+$/.test(staff.user_name.trim())) {
      errors.user_name = "Name can only contain letters and spaces"
    }

    // Email validation
    if (!staff.email?.trim()) {
      errors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(staff.email.trim())) {
      errors.email = "Please enter a valid email address"
    }

    // Mobile validation
    if (!staff.mobile?.trim()) {
      errors.mobile = "Mobile number is required"
    } else if (!/^[6-9]\d{9}$/.test(staff.mobile.trim())) {
      errors.mobile = "Please enter a valid 10-digit mobile number"
    }

    return errors
  }

  const handleEditClick = (staff) => {
    setEditingId(staff.user_id)
    setEditedStaff({ ...staff })
    setValidationErrors({})
  }

  const handleEditChange = (field, value) => {
    let nextValue = value
    if (field === "user_name") {
      nextValue = sanitizeNameInput(value)
    }
    if (field === "mobile") {
      nextValue = String(value || "").replace(/\D/g, "").slice(0, 10)
    }

    setEditedStaff((prev) => ({ ...prev, [field]: nextValue }))

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditedStaff({})
    setValidationErrors({})
  }

  const getDeactivateErrorMessage = (error) => {
    const apiError = error?.response?.data

    if (apiError) {
      const activeTickets = Number(apiError.active_ticket_count || 0)
      const activeInquiries = Number(apiError.active_inquiry_count || 0)
      return `This staff has ${activeTickets} active tickets and ${activeInquiries} active inquiries. Please reassign before deactivation.`
    }

    return error?.message || "Something went wrong"
  }

  const handleDeactivate = async (userId, userName) => {
    try {
      const response = await softDeleteStaff(userId)
      const responseData = response?.data ?? response

      if (responseData?.status === "success") {
        toast.success(`${userName} has been deactivated successfully.`)
        fetchStaff()
        return true
      }

      throw new Error(responseData?.message || "Failed")
    } catch (error) {
      toast.error(getDeactivateErrorMessage(error))
      return false
    }
  }

  const handleSaveChanges = async () => {
    if (!editedStaff.user_id) {
      toast.error("Missing user ID!")
      return
    }

    // Validate form data
    const errors = validateStaffData(editedStaff)
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      toast.error("Please fix the validation errors before saving.")
      return
    }

    // Check for duplicate email (excluding current user)
    const duplicateEmail = staffList.find(
      (staff) => staff.email === editedStaff.email.trim() && staff.user_id !== editedStaff.user_id,
    )
    if (duplicateEmail) {
      setValidationErrors({ email: "This email is already in use by another staff member" })
      toast.error("Email address is already in use.")
      return
    }

    // Check for duplicate mobile (excluding current user)
    const duplicateMobile = staffList.find(
      (staff) => staff.mobile === editedStaff.mobile.trim() && staff.user_id !== editedStaff.user_id,
    )
    if (duplicateMobile) {
      setValidationErrors({ mobile: "This mobile number is already in use by another staff member" })
      toast.error("Mobile number is already in use.")
      return
    }

    const currentStaff = staffList.find((staff) => String(staff.user_id) === String(editedStaff.user_id))
    const originalStatus = Number(currentStaff?.status)
    const nextStatus = Number(editedStaff.status)

    setIsUpdating(true)
    try {
      if (nextStatus === 0 && originalStatus !== 0) {
        const deactivated = await handleDeactivate(editedStaff.user_id, editedStaff.user_name?.trim() || currentStaff?.user_name || "Staff member")
        if (deactivated) {
          setEditingId(null)
          setValidationErrors({})
        } else {
          setEditedStaff((prev) => ({ ...prev, status: currentStaff?.status ?? 1 }))
        }
        return
      }

      const updateData = {
        p_user_id: editedStaff.user_id,
        p_user_name: editedStaff.user_name.trim(),
        p_email: editedStaff.email.trim(),
        p_mobile: editedStaff.mobile.trim(),
        p_status: nextStatus,
        p_client_id: clientId,
      }

      await updateStaff(updateData)
      toast.success("Staff member updated successfully!")
      setEditingId(null)
      setValidationErrors({})
      fetchStaff()
    } catch (error) {
      toast.error("Update failed. Please try again!")
      console.error("Update error:", error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteStaff = async (userId, userName) => {
    const toastId = toast(
      ({ closeToast }) => (
        <div>
          <p className="mb-2">
            Are you sure you want to deactivate <strong>{userName}</strong>?
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-sm btn-danger"
              onClick={async () => {
                const deactivated = await handleDeactivate(userId, userName)
                if (deactivated) {
                  toast.dismiss(toastId)
                } else {
                  return
                }
              }}
            >
              Yes, Deactivate
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => toast.dismiss(toastId)}>
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
      },
    )
  }

  const handleDownloadExcel = () => {
    if (filteredStaff.length === 0) {
      toast.info("No staff data available to export.")
      return
    }

    const exportData = filteredStaff.map((staff) => ({
      "Staff ID": staff.user_id,
      Name: staff.user_name,
      Email: staff.email,
      Mobile: staff.mobile,
      Status: staff.status === 1 ? "Active" : "Inactive",
      "Created Date": staff.created_at ? new Date(staff.created_at).toLocaleDateString() : "N/A",
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "StaffData")
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
    saveAs(data, `Staff_List_${new Date().toISOString().split("T")[0]}.xlsx`)
    toast.success("Excel file downloaded successfully!")
  }

  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage)
  const paginatedStaff = filteredStaff.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const getStatusBadge = (status) => {
    return status === 1 ? (
      <span className="badge bg-success">Active</span>
    ) : (
      <span className="badge bg-danger">Inactive</span>
    )
  }

  return (
    <div className="container-fluid py-4 all-staff-page">
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar />

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm all-staff-hero">
            <div className="card-body">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div>
                  <h4 className="mb-1">
                    <FaUsers className="me-2" />
                    Staff Management
                  </h4>
                  <p className="mb-0 text-muted">Manage staff records, profile details, and activation state.</p>
                </div>
                <button className="btn btn-success rounded-pill px-3 mt-2 mt-md-0" onClick={handleDownloadExcel}>
                  <FaDownload className="me-2" />
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm all-staff-filter-card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <FaSearch className="me-1" />
                    Search Staff
                  </label>
                  <input
                    type="text"
                    className="form-control all-staff-input"
                    placeholder="Search by name, email, or mobile number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Filter by Status</label>
                  <select
                    className="form-select all-staff-input"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active Only</option>
                    <option value="0">Inactive Only</option>
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="text-muted">
                    <small>
                      Showing {paginatedStaff.length} of {filteredStaff.length} staff members
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading staff data...</p>
        </div>
      ) : (
        <div className="card border-0 shadow-sm all-staff-table-card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 all-staff-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Mobile</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStaff.length > 0 ? (
                    paginatedStaff.map((staff) => (
                      <tr key={staff.user_id} className={editingId === staff.user_id ? "table-warning" : ""}>
                        <td className="px-4 py-3">
                          {editingId === staff.user_id ? (
                            <div>
                              <input
                                type="text"
                                className={`form-control all-staff-input-sm ${validationErrors.user_name ? "is-invalid" : ""}`}
                                value={editedStaff.user_name || ""}
                                onChange={(e) => handleEditChange("user_name", e.target.value)}
                                placeholder="Enter full name"
                              />
                              {validationErrors.user_name && (
                                <div className="invalid-feedback">{validationErrors.user_name}</div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div className="fw-semibold">{staff.user_name}</div>
                              <small className="text-muted">ID: {staff.user_id}</small>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {editingId === staff.user_id ? (
                            <div>
                              <input
                                type="email"
                                className={`form-control all-staff-input-sm ${validationErrors.email ? "is-invalid" : ""}`}
                                value={editedStaff.email || ""}
                                onChange={(e) => handleEditChange("email", e.target.value)}
                                placeholder="Enter email address"
                              />
                              {validationErrors.email && (
                                <div className="invalid-feedback">{validationErrors.email}</div>
                              )}
                            </div>
                          ) : (
                            staff.email
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {editingId === staff.user_id ? (
                            <div>
                              <input
                                type="tel"
                                className={`form-control all-staff-input-sm ${validationErrors.mobile ? "is-invalid" : ""}`}
                                value={editedStaff.mobile || ""}
                                onChange={(e) => handleEditChange("mobile", e.target.value)}
                                placeholder="Enter 10-digit mobile number"
                                maxLength="10"
                              />
                              {validationErrors.mobile && (
                                <div className="invalid-feedback">{validationErrors.mobile}</div>
                              )}
                            </div>
                          ) : (
                            staff.mobile
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {editingId === staff.user_id ? (
                            <select
                              className="form-select all-staff-input-sm"
                              value={editedStaff.status}
                              onChange={(e) => handleEditChange("status", Number.parseInt(e.target.value))}
                            >
                              <option value={1}>Active</option>
                              <option value={0}>Inactive</option>
                            </select>
                          ) : (
                            getStatusBadge(staff.status)
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {editingId === staff.user_id ? (
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className="btn btn-success btn-sm rounded-pill px-3"
                                onClick={handleSaveChanges}
                                disabled={isUpdating}
                                title="Save changes"
                              >
                                {isUpdating ? (
                                  <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                  <FaSave />
                                )}
                              </button>
                              <button
                                className="btn btn-secondary btn-sm rounded-pill px-3"
                                onClick={handleCancelEdit}
                                disabled={isUpdating}
                                title="Cancel editing"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                onClick={() => handleEditClick(staff)}
                                title="Edit staff member"
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                onClick={() => handleDeleteStaff(staff.user_id, staff.user_name)}
                                title="Deactivate staff member"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-5">
                        <div className="text-muted">
                          <FaUsers className="fs-1 mb-3 opacity-50" />
                          <p className="mb-0">No staff members found.</p>
                          {searchTerm && <small>Try adjusting your search criteria.</small>}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="card-footer bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    <small>
                      Page {currentPage} of {totalPages} ({filteredStaff.length} total records)
                    </small>
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber
                        if (totalPages <= 5) {
                          pageNumber = i + 1
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i
                        } else {
                          pageNumber = currentPage - 2 + i
                        }
                        return (
                          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? "active" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(pageNumber)}>
                              {pageNumber}
                            </button>
                          </li>
                        )
                      })}
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .all-staff-page {
          --staff-bg: #f4f8ff;
          --staff-border: #dbe4f0;
          background:
            radial-gradient(circle at 6% 0%, rgba(14, 165, 233, 0.14), transparent 28%),
            radial-gradient(circle at 94% 22%, rgba(37, 99, 235, 0.1), transparent 32%),
            var(--staff-bg);
          border-radius: 18px;
        }
        .all-staff-hero {
          border: 1px solid var(--staff-border);
          background: linear-gradient(145deg, #ffffff 0%, #f8fbff 100%);
        }
        .all-staff-filter-card,
        .all-staff-table-card {
          border: 1px solid var(--staff-border);
          background: #ffffff;
        }
        .all-staff-input {
          min-height: 44px;
          border-radius: 12px;
          border-color: #d4deea;
        }
        .all-staff-input-sm {
          min-height: 38px;
          border-radius: 10px;
          border-color: #d4deea;
        }
        .all-staff-input:focus,
        .all-staff-input-sm:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
        }
        .all-staff-table thead th {
          background: linear-gradient(90deg, #0f172a 0%, #1e293b 100%);
          color: #f8fafc;
          border-bottom-width: 0;
        }
        .all-staff-table tbody tr {
          transition: background-color 0.18s ease;
        }
        .all-staff-table tbody tr:hover {
          background: #f3f8ff;
        }
      `}</style>
    </div>
  )
}

export default AllStaff
